// pages/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Skeleton,
  Paper,
  Fade,
  Grow,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Avatar,
  Snackbar,
  Alert,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  DeliveryDining as DeliveryIcon,
  AccessTime as TimeIcon,
  Google as GoogleIcon,
  Store as StoreIcon,
  Storefront as StorefrontIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  WatchLater as WatchLaterIcon,
  QrCodeScanner as QrCodeIcon,
  PointOfSale as PosIcon,
  TableRestaurant as TableRestaurantIcon,
  Analytics as AnalyticsIcon,
  CloudSync as SyncIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
  PlayArrow as PlayArrowIcon,
  Science as ScienceIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';
import WelcomePopup from '../components/WelcomePopup';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import GoogleMapModal from '../components/GoogleMapModal';
import currencyTrans from '../components/CarrancyTrans';
import { useAuth } from '../context/AuthContext';
import noPhoto from '../assets/no_photo.webp';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const HomePage = ({ keywordsData, language }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const { toggleLanguageMen } = useLanguage();
  const searchRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { user, login } = useAuth();
  const isRtl = language === 'ar';

  // Design tokens
  const goldAccent = '#E6B800';
  const goldBright = '#FFD700';
  const softGold = 'rgba(230, 184, 0, 0.12)';
  const surface = 'rgba(20, 20, 20, 0.75)';
  const cardBg = 'rgba(255, 255, 255, 0.025)';
  const border = 'rgba(230, 184, 0, 0.15)';

  // Location states
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [isSelectMap, setIsSelectMap] = useState(false);
  const [openWelcomeModel, setOpenWelcomeModel] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Restaurant states
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gpsChecking, setGpsChecking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Recently viewed
  const [recentStores, setRecentStores] = useState([]);

  // Video dialog
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const videoUrl = 'https://www.youtube.com/embed/TZC5n1UB-QM?si=9hOTAiG0oelZ6FBN';

  useEffect(() => {
    if (lang) toggleLanguageMen(lang);
  }, [lang]);

  const features = [
    { icon: <QrCodeIcon />, title: 'QR Ordering', descAr: 'مسح QR كود لعرض المنيو والطلب مباشرة', descEn: 'Scan QR to view menu and order instantly', color: '#FF6B6B' },
    { icon: <PosIcon />, title: 'POS System', descAr: 'نظام نقاط بيع متكامل لإدارة المبيعات والمخزون', descEn: 'Integrated POS for sales and inventory', color: '#4ECDC4' },
    { icon: <TableRestaurantIcon />, title: 'Table Management', descAr: 'إدارة الطاولات وتتبع الطلبات بكفاءة', descEn: 'Manage tables and track orders efficiently', color: '#FFD93D' },
    { icon: <AnalyticsIcon />, title: 'Advanced Analytics', descAr: 'تقارير متقدمة للمبيعات والأرباح والأداء', descEn: 'Advanced reports for sales and performance', color: '#6C5CE7' },
    { icon: <SyncIcon />, title: 'Cloud Sync', descAr: 'مزامنة فورية بين المنصة ونظام نقاط البيع', descEn: 'Real-time sync across the platform', color: '#A8E6CF' },
    { icon: <PrintIcon />, title: 'Smart Printing', descAr: 'طباعة ذكية حسب الأقسام مع دعم طابعات متعددة', descEn: 'Smart section printing with multi-printer', color: '#FF8A80' },
    { icon: <PaymentIcon />, title: 'Multiple Payments', descAr: 'طرق دفع متعددة مع خصومات ورسوم مرنة', descEn: 'Multiple payment methods with flexibility', color: '#81D4FA' },
    { icon: <InventoryIcon />, title: 'Inventory', descAr: 'إدارة المخزون مع تنبيهات للمنتجات منخفضة المخزون', descEn: 'Inventory with low-stock alerts', color: '#FFCC80' },
  ];

  const stats = [
    { value: '500+', labelAr: 'متاجر تستخدم المنصة', labelEn: 'Stores on board' },
    { value: '50K+', labelAr: 'طلب شهري', labelEn: 'Monthly orders' },
    { value: '98%', labelAr: 'رضا العملاء', labelEn: 'Customer satisfaction' },
    { value: '24/7', labelAr: 'دعم فني', labelEn: 'Live support' },
  ];

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Recent stores
  useEffect(() => {
    const saved = localStorage.getItem('recent_stores');
    if (saved) {
      try { setRecentStores(JSON.parse(saved).slice(0, 10)); } catch (e) { console.error(e); }
    }
  }, []);

  const addToRecentStores = (store) => {
    setRecentStores(prev => {
      const filtered = prev.filter(s => s.id !== store.id);
      const next = [store, ...filtered].slice(0, 10);
      localStorage.setItem('recent_stores', JSON.stringify(next));
      return next;
    });
  };

  const clearRecentStores = () => {
    setRecentStores([]);
    localStorage.removeItem('recent_stores');
  };

  const removeFromRecent = (storeId, e) => {
    e.stopPropagation();
    setRecentStores(prev => {
      const next = prev.filter(s => s.id !== storeId);
      localStorage.setItem('recent_stores', JSON.stringify(next));
      return next;
    });
  };

  // Google login
  const handleGoogleLogin = async (response) => {
    setGoogleLoading(true);
    try {
      const res = await api.post('/auth/google', {
        credential: response.credential,
        client_id: '387866063920-1bfr2kigso4o76ft4nh2fr3i5m61elee.apps.googleusercontent.com',
      });
      if (res.data.success) {
        await login(res.data.token, res.data.user);
      }
    } catch (err) {
      console.error('Google login error:', err);
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && !user) {
        window.google.accounts.id.initialize({
          client_id: '387866063920-1bfr2kigso4o76ft4nh2fr3i5m61elee.apps.googleusercontent.com',
          callback: handleGoogleLogin,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin',
        });
        window.google.accounts.id.prompt((n) => {
          if (n.isNotDisplayed()) console.log('One-tap not displayed:', n.getNotDisplayedReason());
        });
      }
    };
    document.body.appendChild(script);
    return () => {
      if (window.google?.accounts?.id) window.google.accounts.id.cancel();
      document.body.removeChild(script);
    };
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) return callback();
    if (!document.getElementById('google-maps-script')) {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_MAP_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => callback();
      document.body.appendChild(script);
    } else {
      setTimeout(() => window.google?.maps && callback(), 1000);
    }
  };

  const fetchPlaces = async (input) => {
    if (isSelectMap) return;
    try {
      const response = await api.get(`/autocomplete?input=${input}&language=${language}`);
      const data = response.data;
      if (data.predictions) {
        setSuggestions(data.predictions.map((p) => p.description));
        setShowSuggestions(true);
      }
    } catch (err) { console.error(err); }
  };

  const handleLocationSelect = (locationData) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(locationData));
      return;
    }
    if (locationData.address) {
      setQuery(locationData.address);
      localStorage.setItem('user_location', JSON.stringify(locationData));
      setLocation({ lat: locationData.lat, lng: locationData.lng, address: locationData.address });
      fetchNearbyRestaurants(locationData.lat, locationData.lng);
      setIsSelectMap(true);
      setShowSuggestions(false);
    } else {
      setQuery(locationData);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationData }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          setSelectedLocation({ lat: loc.lat(), lng: loc.lng(), address: locationData });
          setOpenMap(true);
        }
      });
      setShowSuggestions(false);
    }
    setSuggestions([]);
  };

  const checkGpsAndOpenMap = () => {
    if (!navigator.geolocation) {
      setSnackbar({
        open: true,
        message: isRtl ? 'المتصفح لا يدعم تحديد الموقع' : 'Browser does not support geolocation',
        severity: 'error',
      });
      return;
    }
    setGpsChecking(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsChecking(false);
        setSelectedLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setOpenMap(true);
      },
      (error) => {
        setGpsChecking(false);
        let msg = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            msg = isRtl
              ? '⚠️ تم رفض الوصول إلى الموقع. يرجى تمكين خدمات الموقع'
              : '⚠️ Location access denied. Please enable location services';
            break;
          case error.POSITION_UNAVAILABLE:
            msg = isRtl
              ? '⚠️ معلومات الموقع غير متوفرة'
              : '⚠️ Location information unavailable';
            break;
          case error.TIMEOUT:
            msg = isRtl ? '⚠️ انتهى وقت المحاولة' : '⚠️ Location request timed out';
            break;
          default:
            msg = isRtl
              ? '⚠️ يرجى تمكين خدمات الموقع (GPS)'
              : '⚠️ Please enable location services (GPS)';
        }
        setSnackbar({ open: true, message: msg, severity: 'warning' });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    clearTimeout(window.debounceTimeout);
    window.debounceTimeout = setTimeout(() => {
      if (value.length > 2) fetchPlaces(value);
      else { setSuggestions([]); setShowSuggestions(false); }
    }, 500);
  };

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  const fetchNearbyRestaurants = async (lat, lng) => {
    try {
      setLoading(true);
      const response = await api.get('/restaurants/nearby', {
        params: { lat, lng, radius: 10, sortBy: 'distance', page: 1, limit: 20 },
      });
      if (response.data.success) setNearbyRestaurants(response.data.restaurants || []);
    } catch (err) {
      console.error('Error fetching nearby restaurants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToShopOwner = () => navigate('/shop-owner-request');

  const handleRestaurantClick = (restaurant) => {
    addToRecentStores({
      id: restaurant.id,
      name: restaurant.name,
      name_en: restaurant.name_en,
      logo: restaurant.logo || restaurant.cover_image,
      cuisine_type: restaurant.cuisine_type,
      rating: restaurant.rating,
    });
    navigate(`/restaurant/${restaurant.id}`);
  };

  // TEST RESTAURANT — quick demo button
  const handleTestRestaurant = () => {
    if (nearbyRestaurants.length > 0) {
      handleRestaurantClick(nearbyRestaurants[0]);
    } else {
      // Fallback: try common demo path
      navigate('/restaurant/2');
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const savedLocation = localStorage.getItem('user_location');
        if (savedLocation) {
          const loc = JSON.parse(savedLocation);
          setLocation(loc);
          setQuery(loc.address);
          fetchNearbyRestaurants(loc.lat, loc.lng);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleOpenVideo = () => setVideoDialogOpen(true);
  const handleCloseVideo = () => setVideoDialogOpen(false);

  // Shared primary button sx
  const primaryBtnSx = {
    background: `linear-gradient(135deg, ${goldAccent} 0%, ${goldBright} 100%)`,
    color: '#111',
    px: { xs: 3, sm: 4 },
    py: { xs: 1.1, sm: 1.3 },
    borderRadius: '999px',
    fontWeight: 800,
    fontSize: { xs: '0.82rem', sm: '0.9rem' },
    textTransform: 'none',
    boxShadow: '0 8px 24px rgba(230, 184, 0, 0.28)',
    transition: 'all 0.25s ease',
    '&:hover': {
      background: `linear-gradient(135deg, ${goldBright} 0%, ${goldAccent} 100%)`,
      boxShadow: '0 12px 32px rgba(230, 184, 0, 0.45)',
      transform: 'translateY(-2px)',
    },
  };

  const secondaryBtnSx = {
    borderColor: 'rgba(230, 184, 0, 0.4)',
    color: goldBright,
    px: { xs: 3, sm: 4 },
    py: { xs: 1.1, sm: 1.3 },
    borderRadius: '999px',
    fontWeight: 700,
    fontSize: { xs: '0.82rem', sm: '0.9rem' },
    textTransform: 'none',
    transition: 'all 0.25s ease',
    '&:hover': {
      borderColor: goldBright,
      backgroundColor: softGold,
      transform: 'translateY(-2px)',
    },
  };

  return (
    <Box
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{
        backgroundColor: '#0A0A0A',
        minHeight: '100vh',
        pb: { xs: 12, md: 14 },
        overflowX: 'hidden',
      }}
    >
      {/* ============ HERO ============ */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, sm: 8, md: 11 },
          overflow: 'hidden',
          background: `
            radial-gradient(circle at 15% 10%, rgba(230,184,0,0.1) 0%, transparent 45%),
            radial-gradient(circle at 85% 90%, rgba(230,184,0,0.06) 0%, transparent 45%),
            linear-gradient(180deg, #0A0A0A 0%, #111 100%)
          `,
          borderBottom: '1px solid rgba(230,184,0,0.1)',
        }}
      >
        {/* Floating orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            [isRtl ? 'left' : 'right']: '-10%',
            width: 320,
            height: 320,
            background: 'radial-gradient(circle, rgba(230,184,0,0.12), transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(40px)',
            animation: 'float 12s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-15%',
            [isRtl ? 'right' : 'left']: '-8%',
            width: 240,
            height: 240,
            background: 'radial-gradient(circle, rgba(230,184,0,0.08), transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(40px)',
            animation: 'float 9s ease-in-out infinite reverse',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center' }}>
            <Grow in timeout={700}>
              <Chip
                label={isRtl ? '🚀 منصة QR Table المتكاملة' : '🚀 QR Table Integrated Platform'}
                sx={{
                  bgcolor: softGold,
                  color: goldBright,
                  mb: 3,
                  px: 2,
                  py: 2.2,
                  fontSize: { xs: '0.7rem', sm: '0.78rem' },
                  fontWeight: 700,
                  border: `1px solid ${border}`,
                  borderRadius: '999px',
                  '& .MuiChip-label': { fontWeight: 700 },
                }}
              />
            </Grow>

            <Grow in timeout={900}>
              <Typography
                variant="h1"
                sx={{
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #FFD700 45%, #E6B800 75%, #FFD700 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: { xs: '1.9rem', sm: '2.6rem', md: '3.6rem' },
                  fontWeight: 900,
                  mb: 2,
                  letterSpacing: { xs: '-0.5px', md: '0.5px' },
                  lineHeight: 1.15,
                }}
              >
                {isRtl ? 'ثورة في عالم المتاجر' : 'Revolutionizing Stores'}
              </Typography>
            </Grow>

            <Fade in timeout={1100}>
              <Typography
                variant="h4"
                sx={{
                  color: '#A8A8A8',
                  fontSize: { xs: '0.9rem', sm: '1.05rem', md: '1.2rem' },
                  fontWeight: 400,
                  mb: 4,
                  maxWidth: 680,
                  mx: 'auto',
                  px: 2,
                  lineHeight: 1.8,
                }}
              >
                {isRtl
                  ? 'منصة QR Table ونظام نقاط البيع المتكامل يجمعان بين الراحة والكفاءة لتجربة تسوق لا تُنسى'
                  : 'QR Table + integrated POS combining convenience and efficiency for an unforgettable ordering experience'}
              </Typography>
            </Fade>

            {/* Stats */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 2, sm: 4, md: 6 },
                mb: 5,
                flexWrap: 'wrap',
              }}
            >
              {stats.map((stat, i) => (
                <Fade in timeout={1400 + i * 180} key={i}>
                  <Box sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: goldBright,
                        fontWeight: 900,
                        fontSize: { xs: '1.4rem', sm: '1.8rem' },
                        letterSpacing: -0.5,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#7A7A7A', fontWeight: 500, display: 'block', mt: 0.5, fontSize: '0.72rem' }}
                    >
                      {isRtl ? stat.labelAr : stat.labelEn}
                    </Typography>
                  </Box>
                </Fade>
              ))}
            </Box>

            {/* CTA Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'center',
                flexWrap: 'wrap',
                px: 2,
              }}
            >
              {user?.role === 'admin' ? (
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                  onClick={() => navigate('/admin')}
                  sx={primaryBtnSx}
                >
                  {isRtl ? 'لوحة تحكم المتجر' : 'Store Dashboard'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                  onClick={() => setOpenWelcomeModel(true)}
                  sx={primaryBtnSx}
                >
                  {isRtl ? 'ابدأ الآن' : 'Get Started'}
                </Button>
              )}

              {/* TEST RESTAURANT button */}
              <Tooltip
                title={isRtl ? 'تجربة مباشرة لمطعم' : 'Try a restaurant instantly'}
                arrow
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ScienceIcon />}
                  onClick={handleTestRestaurant}
                  sx={{
                    ...primaryBtnSx,
                    background: `linear-gradient(135deg, #4ECDC4 0%, #38B2A8 100%)`,
                    boxShadow: '0 8px 24px rgba(78, 205, 196, 0.28)',
                    '&:hover': {
                      background: `linear-gradient(135deg, #38B2A8 0%, #4ECDC4 100%)`,
                      boxShadow: '0 12px 32px rgba(78, 205, 196, 0.45)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {isRtl ? 'تجربة مطعم' : 'Test Restaurant'}
                </Button>
              </Tooltip>

              <Button
                variant="outlined"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={handleOpenVideo}
                sx={secondaryBtnSx}
              >
                {isRtl ? 'شاهد الفيديو' : 'Watch Video'}
              </Button>

              {!user && (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={googleLoading ? <CircularProgress size={18} /> : <GoogleIcon />}
                  disabled={googleLoading}
                  onClick={() => window.google?.accounts?.id?.prompt()}
                  sx={secondaryBtnSx}
                >
                  {isRtl ? 'تسجيل بجوجل' : 'Sign in with Google'}
                </Button>
              )}

              {user && user.role === 'user' && (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<StorefrontIcon />}
                  onClick={handleUpgradeToShopOwner}
                  sx={secondaryBtnSx}
                >
                  {isRtl ? 'الترقية لمتجر' : 'Upgrade to Store'}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ============ FEATURES ============ */}
      <Box sx={{ py: { xs: 5, sm: 7, md: 9 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5, md: 6 } }}>
            <Chip
              label={isRtl ? '✨ المميزات' : '✨ Features'}
              sx={{
                bgcolor: softGold,
                color: goldBright,
                mb: 2,
                fontWeight: 700,
                border: `1px solid ${border}`,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                mb: 2,
                background: 'linear-gradient(135deg, #FFFFFF, #FFD700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '1.6rem', sm: '2.1rem', md: '2.6rem' },
                letterSpacing: -0.5,
              }}
            >
              {isRtl ? 'كل ما تحتاجه في منصة واحدة' : 'Everything in One Platform'}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#7A7A7A', maxWidth: 600, mx: 'auto', px: 2, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
            >
              {isRtl
                ? 'منصة متكاملة تجمع بين نظام QR للطلب ونظام نقاط البيع المتقدم'
                : 'An integrated platform combining QR ordering and advanced POS'}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      bgcolor: cardBg,
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: `1px solid ${border}`,
                      borderRadius: '18px',
                      p: 2.5,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        background: `linear-gradient(90deg, transparent, ${feature.color}80, transparent)`,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        borderColor: `${feature.color}50`,
                        boxShadow: `0 12px 36px ${feature.color}15`,
                        '&::before': { opacity: 1 },
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: `${feature.color}18`,
                        color: feature.color,
                        width: 52,
                        height: 52,
                        mb: 2,
                        '& .MuiSvgIcon-root': { fontSize: 26 },
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{ color: '#FFFFFF', fontSize: '0.98rem', fontWeight: 700, mb: 0.75 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7A7A7A', fontSize: '0.82rem', lineHeight: 1.6 }}
                    >
                      {isRtl ? feature.descAr : feature.descEn}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ============ SEARCH + RESTAURANTS ============ */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Search */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            sx={{ color: '#B8B8B8', mb: 1.5, fontWeight: 600, fontSize: '0.85rem' }}
          >
            {isRtl ? '🔍 ابحث عن مطعم قريب منك' : '🔍 Find a restaurant near you'}
          </Typography>

          <Box ref={searchRef} sx={{ position: 'relative', maxWidth: 560 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <input
                  id="area_search"
                  name="area_search"
                  className="search-input"
                  type="text"
                  placeholder={
                    isRtl
                      ? keywordsData.search_match_location_place_holder_ar
                      : keywordsData.search_match_location_place_holder_en
                  }
                  value={query}
                  onChange={handleQueryChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
              </Box>
              <button
                className="location-btn"
                onClick={checkGpsAndOpenMap}
                aria-label={isRtl ? 'استخدم موقعي' : 'Use my location'}
                disabled={gpsChecking}
              >
                {gpsChecking ? (
                  <CircularProgress size={20} sx={{ color: '#111' }} />
                ) : (
                  <MyLocationIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
                )}
              </button>
            </Box>

            {showSuggestions && suggestions.length > 0 && (
              <Fade in>
                <Box className="suggestions-dropdown" dir={isRtl ? 'rtl' : 'ltr'}>
                  {suggestions.map((place, index) => (
                    <Box
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleLocationSelect(place)}
                    >
                      <LocationIcon
                        sx={{
                          fontSize: 18,
                          color: goldBright,
                          [isRtl ? 'ml' : 'mr']: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                        {place}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Fade>
            )}
          </Box>
        </Box>

        {/* Recently viewed */}
        {recentStores.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WatchLaterIcon sx={{ color: goldBright, fontSize: { xs: 20, sm: 22 } }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  }}
                >
                  {isRtl ? 'المتاجر التي زرتها مؤخراً' : 'Recently Viewed'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={clearRecentStores}
                sx={{
                  color: '#7A7A7A',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': { color: '#FF5252', background: 'transparent' },
                }}
              >
                {isRtl ? 'مسح الكل' : 'Clear all'}
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                overflowX: 'auto',
                pb: 1.5,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.04)', borderRadius: 3 },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(230,184,0,0.4)', borderRadius: 3 },
              }}
            >
              {recentStores.map((store) => (
                <Card
                  key={store.id}
                  onClick={() => navigate(`/restaurant/${store.id}`)}
                  sx={{
                    minWidth: { xs: 130, sm: 150 },
                    maxWidth: { xs: 130, sm: 150 },
                    cursor: 'pointer',
                    bgcolor: cardBg,
                    border: `1px solid rgba(255,255,255,0.06)`,
                    borderRadius: '16px',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: border,
                      boxShadow: '0 12px 28px rgba(0,0,0,0.4)',
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => removeFromRecent(store.id, e)}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      [isRtl ? 'left' : 'right']: 6,
                      zIndex: 2,
                      width: 24,
                      height: 24,
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#B8B8B8',
                      '&:hover': { backgroundColor: '#FF5252', color: '#fff' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>

                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Avatar
                      src={store.logo}
                      sx={{
                        width: 68,
                        height: 68,
                        mx: 'auto',
                        mb: 1,
                        border: `2px solid ${border}`,
                        bgcolor: '#0A0A0A',
                      }}
                    >
                      <StoreIcon sx={{ fontSize: 32, color: goldBright }} />
                    </Avatar>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#FFFFFF',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                      }}
                    >
                      {isRtl ? store.name : store.name_en || store.name}
                    </Typography>
                    {store.rating && (
                      <Chip
                        label={`${store.rating} ⭐`}
                        size="small"
                        sx={{
                          mt: 0.75,
                          background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
                          color: '#111',
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          width: 'fit-content',
                          mx: 'auto',
                        }}
                      />
                    )}
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Current location chip */}
        {location && (
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              p: 1.5,
              borderRadius: '14px',
              bgcolor: cardBg,
              border: `1px solid rgba(255,255,255,0.05)`,
            }}
          >
            <LocationIcon sx={{ color: goldBright, fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#B8B8B8', fontSize: '0.8rem' }}>
              {isRtl ? 'موقعك:' : 'Location:'}
            </Typography>
            <Chip
              label={location.address}
              size="small"
              sx={{
                bgcolor: softGold,
                color: goldBright,
                maxWidth: '100%',
                fontSize: '0.72rem',
                fontWeight: 600,
                '& .MuiChip-label': { whiteSpace: 'normal' },
              }}
            />
          </Box>
        )}

        {/* Restaurant grid */}
        {loading ? (
          <Grid container spacing={{ xs: 2, sm: 2.5 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton
                  variant="rectangular"
                  height={300}
                  sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '20px' }}
                />
              </Grid>
            ))}
          </Grid>
        ) : nearbyRestaurants.length > 0 ? (
          <Grid container spacing={{ xs: 2, sm: 2.5 }}>
            {nearbyRestaurants.map((restaurant) => (
              <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                <RestaurantCard
                  restaurant={restaurant}
                  onClick={() => handleRestaurantClick(restaurant)}
                  language={language}
                  isMobile={isMobile}
                  isRtl={isRtl}
                  goldAccent={goldAccent}
                  goldBright={goldBright}
                  cardBg={cardBg}
                  border={border}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              textAlign: 'center',
              bgcolor: cardBg,
              border: `1px dashed rgba(255,255,255,0.08)`,
              borderRadius: '20px',
            }}
          >
            <StoreIcon sx={{ fontSize: { xs: 60, sm: 72 }, color: 'rgba(255,255,255,0.08)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#B8B8B8', fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              {isRtl ? 'لا توجد متاجر في هذه المنطقة بعد' : 'No stores in this area yet'}
            </Typography>
          </Paper>
        )}
      </Container>

      <WelcomePopup
        isOpen={openWelcomeModel}
        isPart={true}
        onClose={() => setOpenWelcomeModel(false)}
      />

      <GoogleMapModal
        open={openMap}
        handleClose={() => setOpenMap(false)}
        onLocationSelect={(location) => handleLocationSelect(location)}
        initialCenter={selectedLocation}
        language={language}
        keywordsData={keywordsData}
      />

      {/* Video dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleCloseVideo}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: surface,
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: `1px solid ${border}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid rgba(255,255,255,0.06)`,
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: goldBright, fontWeight: 700 }}>
            {isRtl ? 'فيديو توضيحي' : 'Video Tutorial'}
          </Typography>
          <IconButton
            onClick={handleCloseVideo}
            sx={{ color: '#B8B8B8', '&:hover': { color: goldBright } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
          <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%', bgcolor: '#000' }}>
            <iframe
              src={videoUrl}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            bgcolor: surface,
            backdropFilter: 'blur(20px)',
            color: '#FFFFFF',
            border: `1px solid ${
              snackbar.severity === 'success'
                ? 'rgba(76,175,80,0.5)'
                : snackbar.severity === 'warning'
                ? 'rgba(255,152,0,0.5)'
                : 'rgba(244,67,54,0.5)'
            }`,
            borderRadius: '14px',
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; }
          50% { transform: translateY(-20px) scale(1.05); opacity: 1; }
        }

        .search-input {
          width: 100%;
          padding: 13px 20px;
          font-size: 15px;
          background-color: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 999px;
          color: #FFFFFF;
          outline: none;
          transition: all 0.25s ease;
          font-family: inherit;
        }
        .search-input:focus {
          border-color: rgba(230,184,0,0.5);
          background-color: rgba(230,184,0,0.05);
          box-shadow: 0 0 0 4px rgba(230,184,0,0.08);
        }
        .search-input::placeholder {
          color: #6B6B6B;
        }

        .location-btn {
          background: linear-gradient(135deg, #E6B800, #FFD700);
          border: none;
          border-radius: 50%;
          width: 46px;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.25s ease;
          flex-shrink: 0;
          color: #111;
          box-shadow: 0 6px 18px rgba(230,184,0,0.3);
        }
        .location-btn:hover:not(:disabled) {
          transform: translateY(-1px) scale(1.04);
          box-shadow: 0 10px 24px rgba(230,184,0,0.45);
        }
        .location-btn:disabled {
          opacity: 0.7;
          cursor: wait;
        }

        .suggestions-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          background: rgba(20,20,20,0.95);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(230,184,0,0.2);
          border-radius: 16px;
          max-height: 300px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 20px 50px rgba(0,0,0,0.5);
        }
        .suggestion-item {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          color: #FFFFFF;
          font-size: 0.85rem;
        }
        .suggestion-item:hover {
          background-color: rgba(230,184,0,0.1);
        }
        .suggestion-item:first-child { border-top-left-radius: 16px; border-top-right-radius: 16px; }
        .suggestion-item:last-child { border-bottom-left-radius: 16px; border-bottom-right-radius: 16px; }

        @media (max-width: 600px) {
          .search-input { padding: 11px 18px; font-size: 14px; }
          .location-btn { width: 42px; height: 42px; }
        }
      `}</style>
    </Box>
  );
};

/* ============ RestaurantCard ============ */
const RestaurantCard = ({
  restaurant,
  onClick,
  language,
  isMobile,
  isRtl,
  goldAccent,
  goldBright,
  cardBg,
  border,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const isOpen = restaurant.menu_settings?.is_open ?? true;
  const openStatusText = isOpen
    ? language === 'ar'
      ? 'مفتوح'
      : 'Open'
    : language === 'ar'
    ? 'مغلق'
    : 'Closed';
  const openStatusColor = isOpen ? '#4CAF50' : '#FF5252';

  return (
    <Card
      onClick={onClick}
      sx={{
        bgcolor: cardBg,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: `1px solid rgba(255,255,255,0.06)`,
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: border,
          boxShadow: '0 16px 40px rgba(230,184,0,0.12)',
        },
      }}
    >
      {/* Status badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          [isRtl ? 'right' : 'left']: 12,
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: openStatusColor,
          px: 1.25,
          py: 0.35,
          borderRadius: '999px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FFFFFF' }} />
        <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.68rem' }}>
          {openStatusText}
        </Typography>
      </Box>

      {/* Image */}
      <Box sx={{ position: 'relative', height: { xs: 170, sm: 185 }, overflow: 'hidden' }}>
        {!imageLoaded && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.06)',
                borderTopColor: goldBright,
                animation: 'spin 0.8s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </Box>
        )}

        <CardMedia
          component="img"
          image={!imageError ? restaurant.cover_image || restaurant.logo || noPhoto : noPhoto}
          alt={restaurant.name}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.5s ease',
            '&:hover': { transform: 'scale(1.06)' },
          }}
        />

        {/* Dark gradient overlay at bottom */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '50%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)',
            pointerEvents: 'none',
          }}
        />

        {/* Rating */}
        <Chip
          label={`${restaurant.rating || '4.5'} ⭐`}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            [isRtl ? 'left' : 'right']: 12,
            background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
            color: '#111',
            fontWeight: 700,
            fontSize: '0.7rem',
            zIndex: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        />

        {/* Distance */}
        {restaurant.distance && (
          <Chip
            label={`${restaurant.distance.toFixed(1)} km`}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              [isRtl ? 'left' : 'right']: 12,
              bgcolor: 'rgba(0,0,0,0.7)',
              color: goldBright,
              fontWeight: 700,
              backdropFilter: 'blur(8px)',
              fontSize: '0.68rem',
              zIndex: 3,
              border: '1px solid rgba(230,184,0,0.3)',
            }}
          />
        )}
      </Box>

      <CardContent sx={{ p: { xs: 1.75, sm: 2 }, flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#FFFFFF',
            fontWeight: 700,
            mb: 0.5,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: 1.3,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textAlign: isRtl ? 'right' : 'left',
          }}
        >
          {isRtl ? restaurant.name : restaurant.name_en || restaurant.name}
        </Typography>

        {restaurant.cuisine_type && (
          <Typography
            variant="caption"
            sx={{
              color: '#8A8A8A',
              display: 'block',
              mb: 1.25,
              fontSize: '0.72rem',
              textAlign: isRtl ? 'right' : 'left',
            }}
          >
            {isRtl ? restaurant.cuisine_type : restaurant.cuisine_type_en || restaurant.cuisine_type}
          </Typography>
        )}

        {/* Delivery info */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.75,
            flexWrap: 'wrap',
            mb: 1,
            flexDirection: isRtl ? 'row-reverse' : 'row',
            justifyContent: isRtl ? 'flex-end' : 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeIcon sx={{ fontSize: 14, color: goldBright }} />
            <Typography variant="caption" sx={{ color: '#B8B8B8', fontSize: '0.72rem' }}>
              {restaurant.delivery_time_min || '25-35'} min
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DeliveryIcon sx={{ fontSize: 14, color: goldBright }} />
            <Typography variant="caption" sx={{ color: '#B8B8B8', fontSize: '0.72rem' }}>
              {restaurant.delivery_fee || '5'} {currencyTrans(restaurant.currency, language)}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{ color: '#7A7A7A', display: 'block', fontSize: '0.72rem', textAlign: isRtl ? 'right' : 'left' }}
        >
          {isRtl ? 'الحد الأدنى: ' : 'Min: '}
          {restaurant.min_order_amount || '20'} {currencyTrans(restaurant.currency, language)}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default HomePage;